"use client";

import { useTranslation } from "react-i18next";
import { execute, INSERT, SELECT } from "./database_module";
import { Category, Locale, SubCategory, SuperCategory, User } from "./types";
import { logError } from "./helpers";

export function uid() {
  return getRandomString(12);
}
function getRandomString(length: number) {
  return (Math.random() + 1).toString(36).substring(3, length + 3);
}

export function isValidEmail(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidPassword(password: string) {
  const passwordRegex =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+}{"':;?/>,.<|])(?=.*[a-zA-Z]).{8,}$/;
  return passwordRegex.test(password);
}

export function isValidPhoneNumber(phoneNumber: string) {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phoneNumber);
}

export const sendNotification = async (
  title: string,
  message: string,
  image: string,
  page: string,
  deviceToken: string
) => {
  console.log(deviceToken);
  const serverUrl = `${process.env.NEXT_PUBLIC_SOCKET_URL}/send-notification`;
  const body =
    image != ""
      ? {
          target: { token: deviceToken },
          title: title,
          body: message,
          image: image,
          page: page,
        }
      : {
          target: { token: deviceToken },
          title: title,
          body: message,
          page: page,
        };
  try {
    const response = await fetch(serverUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      const result = await response.json();
      console.log("Notification sent successfully:", result);
    } else {
      console.error(
        "Failed to send notification:",
        response.status,
        response.statusText
      );
    }
  } catch (error: any) {
    console.error("Error sending notification:", error.message);
  }
};

export const GetLocale = () => {
  const { i18n } = useTranslation();
  return i18n.language;
};

export const handleEmailClick = (email: string) => {
  window.location.href = `mailto:${email}`;
};

export const handleCall = (phoneNumber: string) => {
  if (phoneNumber.trim() !== "") {
    const telLink = `tel:${phoneNumber}`;
    window.location.href = telLink;
  }
};


export const availableName = (label: any, currentLocale: Locale): string => {
  const isValid = (value: string) => typeof value === 'string' && value.trim() !== '';
  if (isValid(label?.[currentLocale])) {
    return label[currentLocale];
  }

  if (isValid(label?.en)) {
    return label.en;
  }

  if (isValid(label?.fr)) {
    return label.fr;
  }

  return label?.ar || "no_label";
};


const getAllCategories = async () => {
  const categoriesquery = `SELECT categories.*, 
                                super_categories.name AS super_category_name, 
                                COUNT(products.id) AS product_count  
                          FROM categories 
                          JOIN super_categories ON super_categories.id = categories.super_category_id 
                          LEFT JOIN products ON products.category_id = categories.id 
                            AND products.is_deleted = 0  
                          WHERE categories.is_deleted = 0 
                          GROUP BY categories.id, super_categories.name 
                          ORDER BY categories.display_order ASC;
                          `;
  const categories = (await execute(categoriesquery)) as Category[];

  const supercategoriesquery = `SELECT super_categories.*, 
                                      COUNT(products.id) AS product_count 
                                FROM super_categories 
                                LEFT JOIN products ON products.super_category_id = super_categories.id 
                                  AND products.is_deleted = 0 
                                WHERE super_categories.is_deleted = 0  AND super_categories.is_cars = 0
                                GROUP BY super_categories.id 
                                ORDER BY super_categories.display_order ASC;
                                `;
  const super_categories = (await execute(supercategoriesquery)) as Category[];

  const subcategoriesquery = `SELECT sub_categories.*, 
                                    categories.name AS category_name, 
                                    super_categories.id AS super_category_id, 
                                    COUNT(products.id) AS product_count 
                              FROM sub_categories 
                              JOIN categories ON categories.id = sub_categories.category_id 
                              JOIN super_categories ON super_categories.id = categories.super_category_id 
                              LEFT JOIN products ON products.sub_category_id = sub_categories.id 
                                AND products.is_deleted = 0 
                              WHERE sub_categories.is_deleted = 0 
                              GROUP BY sub_categories.id, categories.name, super_categories.id 
                              ORDER BY sub_categories.display_order ASC;
                              `;
  const sub_categories = (await execute(subcategoriesquery)) as Category[];

  const allCategories: {subCategories: SubCategory[]; superCategories: SuperCategory[];categories: Category[];  } = {
        superCategories: super_categories,    
        subCategories: sub_categories,    
        categories: categories,  
      };

  return allCategories;
};

export { getAllCategories };






export function getImgUrl(
  upload: { name: string | null | undefined } | null | undefined
): string {
  const name = upload?.name;
  if (!name) return "";
  return `${process.env.BACKEND_BASE_URL}/images/uploads/${name}`;
}

export function sanitizeFileName(fileName : string) {
  // Define a regular expression for characters to keep
  const illegalChars = /[\/\?<>\\:\*\|":]/g;
  const controlChars = /[\x00-\x1f\x80-\x9f]/g;
  const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;

  let sanitized = fileName.replace(illegalChars, '_')
                          .replace(controlChars, '_')
                          .replace(/ /g, '_');  // Replace spaces with underscores

  // Trim spaces from start and end
  sanitized = sanitized.trim();

  // Ensure the filename is not one of the reserved names in Windows
  if (reservedNames.test(sanitized)) {
      sanitized = '_' + sanitized + '_';
  }

  // Optionally truncate the filename to a maximum length (e.g., 255 characters)
  const maxLength = 255;
  if (sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
}

